import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { isAxiosError } from 'axios';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { AppSpin } from '@/components/atoms';

import TreatmentReportFormSection from '@/components/organisms/TreatmentReportFormSection';
import TreatmentReportFormBottomBar from '@/pages/main/treatmentReport/components/TreatmentReportFormBottomBar';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { useTreatmentReportDetail } from '@/hooks/main/useTreatmentReportDetail';
import { useTreatmentReportList } from '@/hooks/main/useTreatmentReportList';
import { useUpdateTreatmentReport } from '@/hooks/main/useUpdateTreatmentReport';
import { useDeleteTreatmentReport } from '@/hooks/main/useDeleteTreatmentReport';
import { useTreatmentReportRouteParams } from '@/hooks/main/useTreatmentReportRouteParams';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { japaneseDateToISO } from '@/utils/dateConverter';
import {
  fetchTreatmentReportPdf,
  type TreatmentReportUpdatePayload,
} from '@/services/main/treatmentReport';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useClinicPortalServiceType } from '@/hooks/main/clinicPortalServiceType';
import {
  treatmentReportSchema,
  a2TreatmentReportSchema,
  type TreatmentReportFormData,
} from '@/schemas/treatmentReportSchema';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { NO_PRINT_DATA_MESSAGE } from '@/pages/main/treatmentRecord/constants';

export const EditTreatmentReportPage: React.FC = () => {
  const { tenant, id: reportIdParam } = useParams<{ tenant: string; id: string }>();
  const { serviceType: contextServiceType } = useTheme();
  const navigate = useNavigate();
  const toast = useAppToast();

  const isA2 = (contextServiceType?.toUpperCase() ?? '') === SERVICE_TYPE_A2;
  const schema = isA2 ? a2TreatmentReportSchema : treatmentReportSchema;

  const derived = useTreatmentReportRouteParams();
  const { tenant: ctxTenant, clinicNumber } = useClinicContext();

  const { data: listResponse } = useTreatmentReportList(derived.patientId);
  const updateMutation = useUpdateTreatmentReport(derived.patientId);
  const deleteMutation = useDeleteTreatmentReport(derived.patientId);
  const { confirm } = useAppModal();
  const patient = listResponse?.data?.patient;
  const existingReports = listResponse?.data?.treatment_reports ?? [];
  const currentId = reportIdParam ? Number(reportIdParam) : undefined;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { isSubmitting },
  } = useTrimmedForm<TreatmentReportFormData>({
    resolver: zodResolver(schema),
    defaultValues: {},
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const [initialValues, setInitialValues] = useState<Partial<TreatmentReportFormData>>({});
  const [isPrinting, setIsPrinting] = useState(false);
  // Active consent content drives the 変形徒手 / マッサージ classification used by
  // the 5-month-gap FE validation. Seeded from detail; FormSection 再取得 keeps it
  // in sync with the current treatment_report_date.
  const [activeConsentContent, setActiveConsentContent] = useState<string | null>(null);
  const currentValues = watch();
  const watchedYm = watch('treatment_yearmonth');
  const { data: detailResponse, isLoading } = useTreatmentReportDetail(
    derived.patientId,
    reportIdParam,
    { treatmentYearmonth: watchedYm }
  );
  const detail = detailResponse?.data;
  const report = detail?.treatment_report;
  const massagers = detail?.massagers ?? [];

  // Populate form on first load only. Subsequent refetches (triggered by
  // treatment_yearmonth changes to refresh massagers / active_consent_content)
  // must not overwrite the user's edits.
  const hasInitializedFormRef = useRef(false);
  useEffect(() => {
    if (report && !hasInitializedFormRef.current) {
      const vals = {
        treatment_yearmonth: report.treatment_yearmonth,
        treatment_report_date: report.treatment_report_date ?? '',
        contents_frequency: report.contents_frequency ?? '',
        massager_id: report.massager_id ?? (undefined as any),
        clinic_location_id: report.clinic_location_id ?? (undefined as any),
        is_treatment_report_default: report.is_treatment_report_default,
        second_flag: !!report.second_flag,
        hospital_id: report.hospital_id,
        doctor_id: report.doctor_id,
        treatment_monthly_avg: report.treatment_monthly_avg ?? undefined,
        status_course: report.status_course ?? '',
        remarks: report.remarks ?? '',
        location_name: report.address3 ?? '',
        postal_code1: report.postal_code1 ?? '',
        postal_code2: report.postal_code2 ?? '',
        address: report.address ?? '',
        telephone: report.telephone ?? '',
        fax: report.fax ?? '',
        email: report.email ?? '',
        non_print_cols: report.non_print_cols
          ? report.non_print_cols.split(',').map(k => (k === 'address3' ? 'location_name' : k))
          : [],
      };
      reset(vals);
      setInitialValues(vals);
      hasInitializedFormRef.current = true;
    }
  }, [report, reset]);

  useEffect(() => {
    if (detail) {
      setActiveConsentContent(
        (detail as { active_consent_content?: string | null }).active_consent_content ?? null
      );
    }
  }, [detail]);
  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(currentValues, initialValues);

  const isBusy = isSubmitting || updateMutation.isPending;

  const serviceType = useClinicPortalServiceType();
  const handlePrint = useCallback(async () => {
    if (!reportIdParam || !derived.patientId || !ctxTenant || !clinicNumber || isPrinting) return;
    setIsPrinting(true);
    try {
      const blob = await fetchTreatmentReportPdf(
        ctxTenant,
        clinicNumber,
        derived.patientId,
        reportIdParam,
        serviceType
      );
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        toast.error(NO_PRINT_DATA_MESSAGE);
      } else {
        toast.error('PDF生成に失敗しました。');
      }
    } finally {
      setIsPrinting(false);
    }
  }, [reportIdParam, derived.patientId, ctxTenant, clinicNumber, serviceType, toast, isPrinting]);

  const handleDelete = useCallback(() => {
    if (!reportIdParam || isBusy || deleteMutation.isPending) return;
    confirm({
      title: 'この施術報告書を削除してもよろしいですか？',
      okButtonProps: { danger: true },
      okText: 'OK',
      cancelText: 'キャンセル',
      onOk: async () => {
        const res = await deleteMutation.mutateAsync(Number(reportIdParam));
        clearFormChange();
        authorizeNavigation();
        toast.success(res?.message || '削除が正常に終了しました。');
        navigate(`/${tenant}/treatment-reports/${derived.patientId}/list`);
      },
    });
  }, [
    reportIdParam,
    isBusy,
    deleteMutation,
    confirm,
    clearFormChange,
    toast,
    navigate,
    tenant,
    derived.patientId,
  ]);

  const handleBack = useCallback(() => {
    const target = `/${tenant}/treatment-reports/${derived.patientId}/list`;
    if (isBusy) return;
    confirmIfChanged(() => {
      clearFormChange();
      authorizeNavigation();
      navigate(target);
    });
  }, [confirmIfChanged, clearFormChange, navigate, tenant, derived.patientId, isBusy]);

  const validateYearmonthFE = useCallback(
    (data: TreatmentReportFormData): string | null => {
      const ym = data.treatment_yearmonth;
      if (!ym) return null;

      const otherReports = existingReports.filter(r => r.id !== currentId);
      const sameYmReports = otherReports.filter(r => r.treatment_yearmonth === ym);

      if (sameYmReports.length >= 2) {
        return '入力された施術年月の施術報告書は既に２枚作成されています。';
      }
      if (sameYmReports.length >= 1) {
        return '施術年月が重複しています。';
      }

      // 5-month gap — only when 施術報告書交付料を算定しない is unchecked,
      // and skipped entirely for 変形徒手 (R2 with orthomorphia consent).
      const isOrthoType = isA2 ? false : !!activeConsentContent?.includes('変形徒手');
      if (!data.is_treatment_report_default && !isOrthoType) {
        const sorted = otherReports
          .filter(r => r.treatment_yearmonth && r.treatment_yearmonth < ym)
          .sort((a, b) => b.treatment_yearmonth - a.treatment_yearmonth);
        const prev = sorted[0];
        if (prev) {
          const prevYm = prev.treatment_yearmonth;
          const monthDiff =
            (Math.floor(ym / 100) - Math.floor(prevYm / 100)) * 12 + (ym % 100) - (prevYm % 100);
          if (monthDiff < 5) {
            return isA2
              ? '施術報告書交付料算定は、期間を5か月空けて設定してください。'
              : '施術がマッサージの場合の施術報告書交付料算定は、期間を5か月空けて設定してください。';
          }
        }
      }
      return null;
    },
    [existingReports, currentId, isA2, activeConsentContent]
  );

  const onSubmit = useCallback(
    async (data: TreatmentReportFormData) => {
      if (!reportIdParam) return;

      if (!data.second_flag) {
        const feError = validateYearmonthFE(data);
        if (feError) {
          setError('treatment_yearmonth', { message: feError });
          toast.error('更新ができませんでした。');
          return;
        }
      }
      // BD HIT-4357: 施術の内容 comment is optional. If left empty, the printed
      // 施術の内容 is positions-only — warn the user before saving.
      if (!(data.contents_frequency ?? '').trim()) {
        if (!window.confirm('施術報告書の「施術の内容」は施術部位のみです。よろしいですか？')) {
          return;
        }
      }
      const { location_name, clinic_location_id, ...rest } = data;
      // Convert wareki date to ISO for API
      const payload: TreatmentReportUpdatePayload = {
        ...rest,
        clinic_location_id: clinic_location_id ?? null,
        address3: location_name ?? null,
        non_print_cols: rest.non_print_cols?.map(k => (k === 'location_name' ? 'address3' : k)),
        second_flag: rest.second_flag ?? undefined,
        treatment_report_date: rest.treatment_report_date?.includes('年')
          ? japaneseDateToISO(rest.treatment_report_date)
          : rest.treatment_report_date,
      };
      try {
        const res = await updateMutation.mutateAsync({ id: reportIdParam, data: payload });
        clearFormChange();
        authorizeNavigation();
        toast.success(res.message || '更新が正常に終了しました。');
        navigate(`/${tenant}/treatment-reports/${derived.patientId}/list`);
      } catch (err: unknown) {
        const fieldErrors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
          ?.response?.data?.errors;
        if (fieldErrors && typeof fieldErrors === 'object') {
          Object.entries(fieldErrors).forEach(([key, msgs]) => {
            if (Array.isArray(msgs) && msgs[0]) {
              // BE returns the consent-form range error under field treatment_report_date,
              // but BD requires displaying it inline under field 施術年月.
              const targetField = key === 'treatment_report_date' ? 'treatment_yearmonth' : key;
              setError(targetField as keyof TreatmentReportFormData, { message: msgs[0] });
            }
          });
        }
        toast.error('更新ができませんでした。');
      }
    },
    [
      updateMutation,
      clearFormChange,
      validateYearmonthFE,
      setError,
      toast,
      navigate,
      tenant,
      derived.patientId,
      reportIdParam,
    ]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center p-10">
        <AppSpin size="large" />
      </div>
    );
  }

  const formId = 'treatment-report-edit-form';

  return (
    <div>
      <PatientPageHeader
        patientId={derived.patientId}
        tenant={tenant || ''}
        navigate={navigate}
        activeKey="treatment-reports"
        disabled={isBusy}
        patient={patient}
      />

      <form
        id={formId}
        onSubmit={handleSubmit(onSubmit, () => toast.error('更新ができませんでした。'))}
      >
        <TreatmentReportFormSection
          control={control}
          watch={watch}
          setValue={setValue}
          formMode="edit"
          massagers={massagers}
          disabled={isBusy}
          existingReports={existingReports}
          currentReportId={currentId}
          patientId={derived.patientId}
          activeConsentContent={activeConsentContent}
          onActiveConsentChange={setActiveConsentContent}
          initialHospital={
            report?.hospital_id && report?.hospital_name
              ? { id: report.hospital_id, name: report.hospital_name }
              : null
          }
          initialMassager={
            report?.massager_id
              ? { id: report.massager_id, name: report.massager_name, serial_number: null }
              : null
          }
          initialClinicLocation={report?.clinic_location ?? null}
        />
      </form>

      <TreatmentReportFormBottomBar
        mode="edit"
        isSubmitting={isBusy}
        isDeleting={deleteMutation.isPending}
        formId={formId}
        onBack={handleBack}
        onDelete={handleDelete}
        onPrint={handlePrint}
        canPrint={!!reportIdParam && !isPrinting}
      />
    </div>
  );
};
